import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../utils/statisticsUtils';
import useTeamStatistics from '../../hooks/useTeamStatistics';
import useResponsive from '../../hooks/useResponsive';
import {
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { Root, ControlsWrapper, TableWrapper, StyledDataGrid, TeamNameTitle, StatisticsTitle } from './Statistics.styles';

function Statistics({ teamId }) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { period, setPeriod, teamName, rows, loading, members } = useTeamStatistics(teamId);
  const [membersAnchor, setMembersAnchor] = useState(null);

  const periods = ['daily', 'weekly', 'monthly', 'yearly'];

  const handleMembersOpen = (event) => {
    setMembersAnchor(event.currentTarget);
  };

  const handleMembersClose = () => {
    setMembersAnchor(null);
  };

  const membersOpen = Boolean(membersAnchor);

  // Column definitions for the DataGrid, memoized to avoid unnecessary recalcs
  const columns = useMemo(() => [
    { 
      field: 'rank', 
      headerName: t('rank'), 
      width: 100,
      sortable: false
    },
    { 
      field: 'username', 
      headerName: t('usernameCol'), 
      width: 200,
      sortable: false
    },
    { 
      field: 'totalTime', 
      headerName: t('totalStandingTimeCol'), 
      width: 200,
      sortable: false,
      valueFormatter: (value) => formatTime(value),
      
    },
    { 
      field: 'avgTime', 
      headerName: t('averageSessionTimeCol'), 
      width: 200,
      sortable: false,
      valueFormatter: (value) => formatTime(value),
    
    },
    { 
      field: 'longestSession', 
      headerName: t('longestSessionCol'), 
      width: 200,
      sortable: false,
      valueFormatter: (value) => formatTime(value),
    
    },
    { 
      field: 'sessions', 
      headerName: t('totalSessionsCol'), 
      width: 150,
      sortable: false,
      type: 'number'
    }
  ], [t]);

  return (
    <Root maxWidth="xl">
      <StatisticsTitle>
        {t('teamStatistics')} –{' '}
        <Tooltip title={teamName} arrow>
          <TeamNameTitle>{teamName}</TeamNameTitle>
        </Tooltip>
      </StatisticsTitle>

      <ControlsWrapper>
        <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
          {periods.map((p) => (
            <MenuItem key={p} value={p}>
              {t(p)}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title={t('membersTooltip')} arrow>
          <IconButton
            color="primary"
            onClick={handleMembersOpen}
            aria-label={t('membersTooltip')}
            size={isMobile ? 'small' : 'medium'}
          >
            <Badge badgeContent={members.length} max={99} color="secondary">
              <PeopleIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover
          open={membersOpen}
          anchorEl={membersAnchor}
          onClose={handleMembersClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { maxWidth: isMobile ? '90vw' : 300, maxHeight: 320, overflowY: 'auto', p: 1 } }}
        >
          <List dense>
            {members.map((member) => (
              <ListItem key={member.id} disablePadding>
                <Tooltip title={member.username} arrow>
                  <ListItemText
                    primary={member.username}
                    primaryTypographyProps={{ noWrap: true }}
                    sx={{ pr: 1, minWidth: 0 }}
                  />
                </Tooltip>
                {member.isAdmin && (
                  <Chip label={t('admin')} size="small" color="primary" sx={{ ml: 1 }} />
                )}
              </ListItem>
            ))}
          </List>
        </Popover>
      </ControlsWrapper>

      <TableWrapper>
        <StyledDataGrid
           rows={rows}
           columns={columns}
           pageSize={5}
           rowsPerPageOptions={[5, 10, 25]}
           disableColumnMenu
           disableColumnFilter
           disableColumnSelector
           disableSelectionOnClick
           loading={loading}
        />
      </TableWrapper>
    </Root>
  );
}

export default Statistics;